import React, { useState } from 'react'
import Link from 'next/link'
import { useSelector } from 'src/redux/hooks'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Mousewheel } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/mousewheel'
import useStyles from './styles'
import PriceDisplay from 'src/components/PriceDisplay'
import DeliverytimeDisplay from 'src/components/ProductPreview/DeliverytimeDisplay'
import PreviewImage from 'src/components/ProductPreview/PreviewImage'
import Product from 'src/components/ProductPreview/Product'
import productUrl from 'src/functions/productUrl'
import CrossSellProductModal from 'src/pages/Product/Components/CrossSellSections/CrossSellProductModal'
import { tProduct, tProductPreview } from 'src/shared/frontendTypes/product'
import { mergeLivePreviewCommerce, useLiveProductCommerce } from 'src/hooks/useLiveProductCommerce'
import type { imageSrc } from 'src/shared/frontendTypes/images'

SwiperCore.use([Navigation, Mousewheel])

interface Props {
  product: tProduct
  viewType?: 'compact' | 'detailed'
}

const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength).trim() + '...'
  }
  return text
}

const buildPurchasedPreviewImages = (
  purchasedProduct: tProduct,
  combination?: tProduct['combinations'][number],
) => {
  const images: imageSrc[] = []

  if (combination?.images) {
    for (const image of combination.images) {
      images.push({ ...image, src: 'combinations' })
    }
  }

  const selectedOptions = new Set(combination?.options || [])
  for (const attribute of purchasedProduct.attributes || []) {
    for (const option of attribute.options || []) {
      if (!selectedOptions.has(option.id) || !option.images) continue

      for (const image of option.images) {
        images.push({ ...image, src: 'options' })
      }
    }
  }

  for (const image of purchasedProduct.images || []) {
    if (image.type !== 'mood') images.push({ ...image, src: 'product' })
  }

  return images
}

interface PurchasedCardProps {
  purchasedProduct: tProduct
  purchasedPreview: tProductPreview
  viewType: 'compact' | 'detailed'
  expanded: boolean
  onToggle: (id: number) => void
  onOpen: (product: tProduct, preview: tProductPreview) => void
}

const buildPreviewFromProduct = (purchasedProduct: tProduct): tProductPreview => {
  const combination = purchasedProduct.combinations
    .slice()
    .sort((a, b) => {
      const aPrice = a.discount?.price ?? a.price
      const bPrice = b.discount?.price ?? b.price
      if (aPrice !== bPrice) return aPrice - bPrice

      const aStock = a.stock || 0
      const bStock = b.stock || 0
      return bStock - aStock
    })[0]

  return {
    id: purchasedProduct.id,
    categorie: purchasedProduct.categorie,
    key: combination?.key || String(purchasedProduct.id),
    name: purchasedProduct.name,
    price: combination?.price || 0,
    deliverytime: combination?.deliverytime || 0,
    ...(typeof combination?.stockDeliverytime === 'number' ? { stockDeliverytime: combination.stockDeliverytime } : {}),
    ...(typeof combination?.stock === 'number' ? { stock: combination.stock } : {}),
    ...(purchasedProduct.purchaseMode ? { purchaseMode: purchasedProduct.purchaseMode } : {}),
    ...(purchasedProduct.onlyStock ? { onlyStock: purchasedProduct.onlyStock } : {}),
    ...(purchasedProduct.inquireOnly ? { inquireOnly: purchasedProduct.inquireOnly } : {}),
    path: purchasedProduct.path,
    metaTitle: purchasedProduct.metaTitle,
    brand: purchasedProduct.brand,
    brandTitel: purchasedProduct.brandTitel,
    brandMetaTitel: purchasedProduct.brandMetaTitel,
    images: buildPurchasedPreviewImages(purchasedProduct, combination),
    ...(combination?.discount ? { discount: combination.discount } : {}),
    shippingType: purchasedProduct.shippingType,
    options: combination?.options || [],
    attributes: combination?.attributes || [],
  }
}

const PurchasedCard: React.FC<PurchasedCardProps> = ({
  purchasedProduct,
  purchasedPreview,
  viewType,
  expanded,
  onToggle,
  onOpen,
}) => {
  
  const touchDevice = useSelector((store) => store.App.isTouchDevice)
  const classes = useStyles({ viewType, touchDevice })
  const livePreview = useLiveProductCommerce(purchasedPreview)
  const previewForRender = livePreview
    ? mergeLivePreviewCommerce(purchasedPreview, livePreview)
    : purchasedPreview

  const url = productUrl(
    previewForRender.id,
    previewForRender.path,
    previewForRender.options
  )
  const previewOptionText = previewForRender.attributes
    .filter(attribute => attribute && attribute.trim().length > 0)
    .join(' · ')
  const priceToDisplay = previewForRender.discount
    ? {
      regularPrice: previewForRender.price,
      discountPrice: previewForRender.discount.price,
      discountPriceAfterStock: previewForRender.discount.priceAfterStock,
      stockDiscountLimit: previewForRender.discount.stockDiscountLimit,
      limitStockDiscountToStock: previewForRender.discount.limitStockDiscountToStock,
      quantityDiscount: previewForRender.discount.quantityDiscount,
      title: previewForRender.discount.metaTitle || '',
      type: previewForRender.discount.type || '',
      countdownVariant: previewForRender.discount.countdownVariant,
      dateEnd: previewForRender.discount.dateEnd || '',
      showPercent: previewForRender.discount.showPercent,
    }
    : previewForRender.price

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (viewType === 'compact') {
      onOpen(purchasedProduct, previewForRender)
    } else {
      onToggle(purchasedProduct.id)
    }
  }

  // Im Detailed-Modus soll die Kachel (wenn ausgeklappt) 2 Zeilen beanspruchen
  const detailedGridStyle =
    viewType === 'detailed' && expanded
      ? { gridColumn: 'span 1', gridRow: 'span 2' }
      : {}

  return (
    <Card key={purchasedProduct.id} className={classes.card} style={detailedGridStyle}>
      <Link href={url}
         prefetch={viewType !== 'compact'}
         className={classes.cardLink}
         style={{ textDecoration: 'none', color: 'inherit' }}
         onClick={handleClick}
         aria-label={classes.name}
        >
          <PreviewImage
            images={previewForRender.images}
            productID={previewForRender.id}
            metaTitle={previewForRender.metaTitle}
            size='xs'
            aspectRatio='1 / 1'
          />
          <CardContent className={classes.cardContent}>
            <Typography 
              align="center" 
              variant="body2" 
              className={classes.metaTitle}
              style={{ textDecoration: 'none' }}
            >
              {viewType === 'compact'
                ? truncateText(previewForRender.metaTitle, 50)
                : previewForRender.metaTitle}
            </Typography>
            {previewOptionText && (
              <Typography
                align="center"
                variant="caption"
                style={{
                  display: 'block',
                  color: '#64748b',
                  marginTop: 4,
                  textDecoration: 'none',
                }}
              >
                {truncateText(previewOptionText, 60)}
              </Typography>
            )}
            <div className={classes.priceContainer}>
              <span className={classes.priceLabel}>ab&nbsp;</span>
              <PriceDisplay 
                price={priceToDisplay} 
                component="span" 
                variant="body2"
                discountBadgeVariant="inline"
                style={{ textDecoration: 'none' }}
                onlyStock={purchasedProduct.onlyStock || previewForRender.onlyStock}
                stock={previewForRender.stock}
              />
            </div>
            <div className={classes.deliverytimeContainer}>
              <DeliverytimeDisplay
                deliverytime={previewForRender.deliverytime}
                stock={previewForRender.stock}
                onlyStock={purchasedProduct.onlyStock}
                inquireOnly={previewForRender.inquireOnly}
              />
            </div>
            {viewType === 'detailed' && expanded && (
              <div className={classes.expandedContent}>
                <Typography variant="caption" align="center">
                  {truncateText(purchasedProduct.descriptionShort || '', 150)}
                </Typography>
              </div>
            )}
          </CardContent>
        </Link>
    </Card>
  )
}

const ProductPurchased: React.FC<Props> = ({ product, viewType = 'compact' }) => {
  const touchDevice = useSelector((store) => store.App.isTouchDevice)
  const classes = useStyles({ viewType, touchDevice })
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [modalProduct, setModalProduct] = useState<tProduct | null>(null)
  const [modalPreview, setModalPreview] = useState<tProductPreview | null>(null)

  if (!product.alsoPurchased || product.alsoPurchased.length === 0) {
    return null
  }

  const validAlsoPurchased = product.alsoPurchased.filter((item: any) => {
    const details = item.productDetails
    return (
      details &&
      details.id &&
      details.metaTitle &&
      details.images?.length > 0 &&
      details.combinations?.length > 0
    )
  })

  if (validAlsoPurchased.length === 0) return null

  const handleToggle = (id: number) => {
    setExpandedId(prev => (prev === id ? null : id))
  }

  const purchasedItems = validAlsoPurchased.map((item: any) => ({
    productDetails: item.productDetails as tProduct,
    preview: buildPreviewFromProduct(item.productDetails),
  }))

  if (viewType === 'compact') {
    return (
      <section className={`${classes.cardColumn} ${classes.purchased}`}>
        <div className={classes.sliderRoot}>
          <Swiper
            navigation
            watchOverflow
            mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
            speed={420}
            allowTouchMove
            simulateTouch
            grabCursor
            resistanceRatio={.2}
            longSwipesRatio={.18}
            longSwipesMs={180}
            slidesPerView={2}
            spaceBetween={12}
            breakpoints={{
              600: {
                slidesPerView: 2.45,
                spaceBetween: 16,
              },
              960: {
                slidesPerView: 3.45,
                spaceBetween: 18,
              },
              1280: {
                slidesPerView: 4.45,
                spaceBetween: 20,
              },
            }}
          >
            {purchasedItems.map(item => (
              <SwiperSlide
                key={[
                  item.productDetails.id,
                  item.preview.key || '',
                  (item.preview.options || []).join('-'),
                ].join(':')}
              >
                <Product
                  product={item.preview}
                  variant='slider'
                  display={['title', 'attributes', 'price', 'deliverytime']}
                  showAvailability
                  hoverEffect={false}
                  imageSizePx={isMobile ? undefined : 148}
                  emphasizeInStock
                  onOpenProduct={(nextPreview) => {
                    setModalProduct(item.productDetails)
                    setModalPreview(nextPreview)
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <CrossSellProductModal
          open={Boolean(modalPreview)}
          preview={modalPreview}
          currentProduct={product}
          prefetchedProduct={modalProduct}
          onClose={() => {
            setModalProduct(null)
            setModalPreview(null)
          }}
        />
      </section>
    )
  }

  return (
    <section className={`${classes.cardColumn} ${classes.purchased}`}>
      <div className={classes.productGrid}>
        {purchasedItems.map(item => (
          <PurchasedCard
            key={item.productDetails.id}
            purchasedProduct={item.productDetails}
            purchasedPreview={item.preview}
            viewType={viewType}
            expanded={viewType === 'detailed' && expandedId === item.productDetails.id}
            onToggle={handleToggle}
            onOpen={(nextProduct, nextPreview) => {
              setModalProduct(nextProduct)
              setModalPreview(nextPreview)
            }}
          />
        ))}
      </div>

      <CrossSellProductModal
        open={Boolean(modalPreview)}
        preview={modalPreview}
        currentProduct={product}
        prefetchedProduct={modalProduct}
        onClose={() => {
          setModalProduct(null)
          setModalPreview(null)
        }}
      />
    </section>
  )
}

export default ProductPurchased
